/* eslint-disable react-refresh/only-export-components */
// context/hook 与组件同文件为有意模式;react-refresh 仅关注 HMR,不影响运行时。
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getToken, setToken, clearToken } from "@/shared/api";
import {
  IMPERSONATION_META_KEY,
  clearImpersonationBrand,
  fetchAndStoreImpersonationBrand,
  normalizeBrandPayload,
} from "@/shared/brand";

/**
 * 代客登录(impersonation)上下文。
 *
 * 平台超管点击"代客登录"后,前端把原 token 存到 ORIGIN_TOKEN_KEY,新 impersonation token
 * 写到主 token 槽。退出代客时还原主 token。
 *
 * Banner 在 App layout 顶部读这里 — 显示"你正在以 X 身份操作 / 倒计时 / 退出"。
 */

const ORIGIN_TOKEN_KEY = "xbgeo-origin-token";

/** 结束代客接口(1004 F1b):用代客 token 调,后端落 impersonate_end(带起始原因 + startAuditId + trigger)。 */
export const IMPERSONATION_END_ENDPOINT = "/api/v1/auth/impersonation/end";
/** 到时自动退出提前量:token 一到期就调不动结束接口了,提前几秒退出,保证 impersonate_end 落得下。 */
export const AUTO_EXIT_LEAD_MS = 5_000;

export type ImpersonationEndTrigger = "manual" | "timeout";

/**
 * 通知后端结束代客(落审计)。不走 httpClient:代客 token 失效时 httpClient 的 401 口会把人整个登出;
 * keepalive = 页面紧接着跳转也能发完。任何失败都吞掉,**不阻塞退出**。
 */
export function notifyImpersonationEnd(
  token: string | null,
  trigger: ImpersonationEndTrigger,
  fetchImpl: typeof fetch | undefined = typeof fetch !== "undefined" ? fetch : undefined
): Promise<void> {
  if (!token || !fetchImpl) return Promise.resolve();
  try {
    return fetchImpl(IMPERSONATION_END_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ trigger }),
      keepalive: true,
    }).then(
      () => undefined,
      () => undefined
    );
  } catch {
    return Promise.resolve();
  }
}

interface ImpersonationMeta {
  /** V27 (2026-05-20) — 代客租户数字主键, 给 /api/v1/platform/tenants/{tenantId}/* 等带 id 的接口用 */
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  expiresAt: number; // epoch ms
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  meta: ImpersonationMeta | null;
  /** 进入代客模式:存原 token + 切到代客 token。调用方负责拿到 impersonation token + 调本方法。 */
  startImpersonation: (newToken: string, meta: ImpersonationMeta, redirectTo?: string) => void;
  /** 退出代客:通知后端落 impersonate_end(失败不阻塞)+ 恢复原 token + 清 meta。 */
  stopImpersonation: (trigger?: ImpersonationEndTrigger) => void;
}

const Ctx = createContext<ImpersonationContextType | null>(null);

function loadMeta(): ImpersonationMeta | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_META_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw) as ImpersonationMeta;
    if (m.expiresAt < Date.now()) {
      localStorage.removeItem(IMPERSONATION_META_KEY);
      localStorage.removeItem(ORIGIN_TOKEN_KEY);
      clearImpersonationBrand();
      return null;
    }
    return m;
  } catch {
    return null;
  }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<ImpersonationMeta | null>(loadMeta);

  // 到期自动退出(提前 AUTO_EXIT_LEAD_MS:到期后代客 token 已失效,结束接口落不了审计)
  useEffect(() => {
    if (!meta) return;
    const left = meta.expiresAt - AUTO_EXIT_LEAD_MS - Date.now();
    if (left <= 0) {
      stopImpersonation("timeout");
      return;
    }
    const t = setTimeout(() => stopImpersonation("timeout"), left);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.expiresAt]);

  // redirectTo(2026-08-09):代客后的落点,默认首页。知识审核总览「进入审核」要直达
  // /kb/review —— 此前写死 "/",一键直达变成一键回家。只收站内路径(以 / 开头),防 open redirect。
  function startImpersonation(newToken: string, m: ImpersonationMeta, redirectTo = "/") {
    const origin = getToken();
    if (origin) localStorage.setItem(ORIGIN_TOKEN_KEY, origin);
    setToken(newToken);
    localStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify(m));
    setMeta(m);
    // V27 (2026-05-20) — 清掉权限缓存, 让 reload 后 AuthContext useEffect 强制 refetch
    // 目标租户视角的 /me/permissions。 不清就会沿用平台超管的 cache, sidebar 不切。
    localStorage.removeItem("xbgeo-permissions");
    const target = redirectTo.startsWith("/") && !redirectTo.startsWith("//") ? redirectTo : "/";
    // 1004 F1b:先用代客 token 拉被代客租户所属渠道的品牌存档(最多 3s,失败照常进入,沿用域名品牌),
    // reload 后 bootstrapBrand 套它 —— 平台人员看到的就是客户看到的。
    clearImpersonationBrand();
    void fetchAndStoreImpersonationBrand(newToken, normalizeBrandPayload).finally(() =>
      window.location.assign(target)
    );
  }

  function stopImpersonation(trigger?: ImpersonationEndTrigger) {
    // onClick={stopImpersonation} 会把事件对象传进来 —— 只认两个字面量,其余按手动退出。
    const why: ImpersonationEndTrigger = trigger === "timeout" ? "timeout" : "manual";
    // 1004 F1b:退出前用代客 token 通知后端落 impersonate_end(三端审计都出现「代客退出」);不等结果、失败不阻塞。
    void notifyImpersonationEnd(getToken(), why);
    clearImpersonationBrand();
    const origin = localStorage.getItem(ORIGIN_TOKEN_KEY);
    if (origin) {
      setToken(origin);
      localStorage.removeItem(ORIGIN_TOKEN_KEY);
    } else {
      clearToken();
    }
    localStorage.removeItem(IMPERSONATION_META_KEY);
    // V27 — 退出代客同理, 清权限 cache, reload 后 refetch 平台超管视角
    localStorage.removeItem("xbgeo-permissions");
    setMeta(null);
    window.location.assign("/");
  }

  const value = useMemo<ImpersonationContextType>(
    () => ({
      isImpersonating: !!meta,
      meta,
      startImpersonation,
      stopImpersonation,
    }),
    [meta]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useImpersonation() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useImpersonation must be used within ImpersonationProvider");
  return ctx;
}

/**
 * 非抛错版本:Provider 缺失时返回安全默认(非代客)。
 * 用于在 Provider 树外也可能渲染的通用组件(如 AppSidebar 在单元测试里
 * 不一定挂 ImpersonationProvider),避免硬抛错。
 */
export function useImpersonationOptional(): ImpersonationContextType {
  const ctx = useContext(Ctx);
  return (
    ctx ?? {
      isImpersonating: false,
      meta: null,
      startImpersonation: () => undefined,
      stopImpersonation: () => undefined,
    }
  );
}
